// import components
import { Checkbox, MenuItem } from '@mui/material';
import VerticalToolbarActionWrapper from 'components/shared/ToolbarSet/components/VerticalToolbarActionWrapper';

// import external libraries
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// import styles
import {
  BacklogCheckboxRow,
  BacklogControlStack,
  BacklogFieldContainer,
  BacklogPopoverTitle,
  BacklogToolbarField,
  ViewSettingCheckboxLabel
} from '../../Backlog.styles';

// import types
import type { BacklogCellValueMode } from '../../Backlog.types';
import type { BacklogViewSettingsProps } from './BacklogViewSettings.types';

/**
 * The eye icon in the toolbar's left controls: everything that changes *how* the same tasks are drawn,
 * as opposed to which ones are listed. What the cells read, and whether fulfilled weeks are shown.
 */
const BacklogViewSettings = ({
  cellValueMode,
  onCellValueModeChange,
  showTotals,
  onShowTotalsChange,
  showCompleted,
  onShowCompletedChange,
  isRightPositioned
}: BacklogViewSettingsProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'backlog' });

  const cellValueOptions = useMemo(
    () => [
      { value: 'difference', label: t('cellContent.difference') },
      {
        value: 'plannedOverPrescribed',
        label: t('cellContent.plannedOverPrescribed')
      }
    ],
    [t]
  );

  /**
   * The same borderless toolbar field the two filters render, so all three dropdowns match: the plan unit
   * selector's StyledCustomTextField with isToolbar, its label floating above the value. In select mode
   * rather than an autocomplete, since this is one choice out of two fixed options with nothing to
   * search.
   *
   * Not components/shared/Select, which passes htmlFor={label} — the label text, not an element id — so
   * its field ends up with no accessible name at all.
   */
  const cellValueSelect = (
    <BacklogFieldContainer>
      <BacklogToolbarField
        select
        label={t('cellContent.label')}
        isToolbar
        variant="standard"
        size="small"
        fullWidth
        slotProps={{ inputLabel: { shrink: true } }}
        value={cellValueMode}
        onChange={(event) =>
          onCellValueModeChange(event.target.value as BacklogCellValueMode)
        }
      >
        {cellValueOptions.map(({ value, label }) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </BacklogToolbarField>
    </BacklogFieldContainer>
  );

  /**
   * One helper for both checkboxes, so the pair cannot drift apart: the same compact checkbox with its
   * label beside it, at caption size to sit with the rest of the popover.
   */
  const renderCheckbox = (
    label: string,
    checked: boolean,
    onChange: (next: boolean) => void
  ) => (
    <ViewSettingCheckboxLabel
      key={label}
      slotProps={{ typography: { variant: 'body1' } }}
      control={
        <Checkbox
          size="small"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
        />
      }
      label={label}
    />
  );

  // Side by side rather than stacked, so the two read as a related pair on one line.
  const checkboxes = (
    <BacklogCheckboxRow>
      {renderCheckbox(
        t('filters.showCompleted'),
        showCompleted,
        onShowCompletedChange
      )}
      {renderCheckbox(t('filters.showTotals'), showTotals, onShowTotalsChange)}
    </BacklogCheckboxRow>
  );

  // Always a single eye icon that opens the view settings stacked in a popper. Across the top the
  // popper drops down under the icon; docked to the side it opens into the page to its left.
  return (
    <VerticalToolbarActionWrapper
      iconType="eye"
      title={t('viewSettings')}
      placement={isRightPositioned ? 'left' : 'bottom-start'}
      tooltipPlacement={isRightPositioned ? 'left' : 'bottom'}
    >
      <BacklogControlStack>
        <BacklogPopoverTitle variant="subtitle1">
          {t('viewSettings')}
        </BacklogPopoverTitle>
        {cellValueSelect}
        {checkboxes}
      </BacklogControlStack>
    </VerticalToolbarActionWrapper>
  );
};

export default memo(BacklogViewSettings);
